import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as EmpDB from '@/lib/employeeDb';
import { formatTRY } from '@/lib/currencyFormat';
import EmployeeFormModal from '@/components/feature/EmployeeFormModal';
import EmployeePaymentModal from '@/components/feature/EmployeePaymentModal';
import EmployeeLeaveModal from '@/components/feature/EmployeeLeaveModal';
import EmployeeOvertimeModal from '@/components/feature/EmployeeOvertimeModal';
import Badge from '@/components/base/Badge';
import { staffStatus, paymentStatus, deliveryStatus } from '@/lib/statusDefs';

const TABS = [
  { key: 'overview', label: 'Genel Bilgiler', icon: 'ri-user-3-line' },
  { key: 'payments', label: 'Maaş ve Ödemeler', icon: 'ri-money-dollar-circle-line' },
  { key: 'leaves', label: 'İzinler', icon: 'ri-calendar-check-line' },
  { key: 'overtimes', label: 'Mesailer', icon: 'ri-timer-line' },
  { key: 'sales', label: 'Satışlar', icon: 'ri-shopping-cart-2-line' },
  { key: 'deliveries', label: 'Teslimatlar', icon: 'ri-truck-line' },
  { key: 'activity', label: 'Aktivite Geçmişi', icon: 'ri-history-line' },
];

export default function StaffDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [payments, setPayments] = useState<any[]>([]);
  const [leaves, setLeaves] = useState<any[]>([]);
  const [overtimes, setOvertimes] = useState<any[]>([]);
  const [sales, setSales] = useState<any[]>([]);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [showOvertime, setShowOvertime] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: string } | null>(null);

  const showToast = (msg: string, type: string = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadEmployee = useCallback(async () => {
    if (!id) return;
    const { data, error: err } = await EmpDB.getEmployeeById(id);
    if (err || !data) { setError(err || 'Personel bulunamadı'); setLoading(false); return; }
    setEmployee(data);
    setError(null);

    const [statsRes, paymentsRes, leavesRes, overtimesRes, salesRes, deliveriesRes, activityRes] = await Promise.all([
      EmpDB.getEmployeeStats(id),
      EmpDB.getEmployeePayments(id),
      EmpDB.getEmployeeLeaves(id),
      EmpDB.getEmployeeOvertimes(id),
      EmpDB.getEmployeeSales(id),
      EmpDB.getEmployeeDeliveries(id),
      EmpDB.getEmployeeActivityHistory(id),
    ]);

    setStats(statsRes.data);
    setPayments(paymentsRes.data || []);
    setLeaves(leavesRes.data || []);
    setOvertimes(overtimesRes.data || []);
    setSales(salesRes.data || []);
    setDeliveries(deliveriesRes.data || []);
    setActivities(activityRes.data || []);
    setLoading(false);
  }, [id]);

  useEffect(() => { loadEmployee(); }, [loadEmployee]);

  const handleEdit = async (data: any) => {
    const { error: err } = await EmpDB.updateEmployee(id!, data);
    if (err) { showToast('Güncelleme başarısız: ' + err, 'error'); return; }
    showToast('Personel güncellendi');
    setShowEdit(false);
    loadEmployee();
  };

  const handlePayment = async (data: any) => {
    const { error: err } = await EmpDB.createEmployeePayment({ employee_id: id, ...data });
    if (err) { showToast('Ödeme kaydedilemedi: ' + err, 'error'); return; }
    showToast('Ödeme kaydedildi');
    setShowPayment(false);
    loadEmployee();
  };

  const handleLeave = async (data: any) => {
    const { error: err } = await EmpDB.createEmployeeLeave({ employee_id: id, ...data });
    if (err) { showToast('İzin kaydedilemedi: ' + err, 'error'); return; }
    showToast('İzin kaydedildi');
    setShowLeave(false);
    loadEmployee();
  };

  const handleOvertime = async (data: any) => {
    const { error: err } = await EmpDB.createEmployeeOvertime({ employee_id: id, ...data });
    if (err) { showToast('Mesai kaydedilemedi: ' + err, 'error'); return; }
    showToast('Mesai kaydedildi');
    setShowOvertime(false);
    loadEmployee();
  };

  const handleDeletePayment = async (pid: string) => {
    if (!confirm('Bu ödeme kaydı silinsin mi?')) return;
    const { error: err } = await EmpDB.deleteEmployeePayment(pid);
    if (err) { showToast('Silinemedi: ' + err, 'error'); return; }
    showToast('Ödeme silindi');
    loadEmployee();
  };

  const handleDeleteLeave = async (lid: string) => {
    if (!confirm('Bu izin kaydı silinsin mi?')) return;
    const { error: err } = await EmpDB.deleteEmployeeLeave(lid);
    if (err) { showToast('Silinemedi: ' + err, 'error'); return; }
    showToast('İzin silindi');
    loadEmployee();
  };

  const handleDeleteOvertime = async (oid: string) => {
    if (!confirm('Bu mesai kaydı silinsin mi?')) return;
    const { error: err } = await EmpDB.deleteEmployeeOvertime(oid);
    if (err) { showToast('Silinemedi: ' + err, 'error'); return; }
    showToast('Mesai silindi');
    loadEmployee();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
        <h2 className="text-lg font-semibold text-foreground-700">Personel bilgileri yükleniyor...</h2>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-3">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center"><i className="ri-error-warning-line text-2xl text-red-400"></i></div>
        <h2 className="text-lg font-semibold text-foreground-700">{error || 'Personel bulunamadı'}</h2>
        <button onClick={() => navigate('/staff')} className="btn-secondary cursor-pointer whitespace-nowrap">Geri Dön</button>
      </div>
    );
  }

  const totalPaid = payments.reduce((s: number, p: any) => s + parseFloat(p.amount || '0'), 0);
  const totalOvertime = overtimes.reduce((s: number, o: any) => s + parseFloat(o.amount || '0'), 0);
  const totalAdvance = payments.filter((p: any) => p.payment_type === 'Avans').reduce((s: number, p: any) => s + parseFloat(p.amount || '0'), 0);

  return (
    <div className="space-y-5">
      {toast && (
        <div className={`fixed top-4 right-4 z-[70] px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all ${
          toast.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/staff')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer">
          <i className="ri-arrow-left-line text-lg text-foreground-500"></i>
        </button>
        <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-3-line text-xl text-primary-600"></i>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">{employee.full_name}</h2>
              <p className="text-sm text-foreground-400">{employee.employee_no} · {employee.position || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge badgeClass={staffStatus.badgeClass(employee.status)} icon={staffStatus.icon(employee.status)}>{employee.status}</Badge>
            <button onClick={() => setShowPayment(true)} className="btn-primary cursor-pointer whitespace-nowrap flex items-center gap-1.5 text-sm">
              <i className="ri-add-line"></i>Ödeme / Avans
            </button>
            <button onClick={() => setShowEdit(true)} className="btn-secondary cursor-pointer whitespace-nowrap flex items-center gap-1.5 text-sm">
              <i className="ri-edit-line"></i>Düzenle
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <div className="stat-card"><p className="text-xs text-foreground-400">Toplam Satış</p><p className="text-lg font-bold text-foreground-900 mt-1">{formatTRY(stats.total_sales)}</p><p className="text-[11px] text-foreground-400">{stats.total_sales_count} satış</p></div>
          <div className="stat-card"><p className="text-xs text-foreground-400">Toplam Tahsilat</p><p className="text-lg font-bold text-emerald-600 mt-1">{formatTRY(stats.total_collections)}</p><p className="text-[11px] text-foreground-400">{stats.total_collections_count} ödeme</p></div>
          <div className="stat-card"><p className="text-xs text-foreground-400">Bu Ay Satış</p><p className="text-lg font-bold text-foreground-900 mt-1">{formatTRY(stats.monthly_sales)}</p></div>
          <div className="stat-card"><p className="text-xs text-foreground-400">Bu Ay Tahsilat</p><p className="text-lg font-bold text-emerald-600 mt-1">{formatTRY(stats.monthly_collections)}</p></div>
          <div className="stat-card"><p className="text-xs text-foreground-400">Teslimat</p><p className="text-lg font-bold text-foreground-900 mt-1">{stats.total_deliveries}</p><p className="text-[11px] text-foreground-400">tamamlanan</p></div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-background-200/70">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === tab.key ? 'border-primary-500 text-primary-600' : 'border-transparent text-foreground-400 hover:text-foreground-600'
            }`}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="stat-card">
              <h3 className="text-sm font-semibold text-foreground-800 mb-4">Kişisel Bilgiler</h3>
              <div className="space-y-3">
                <InfoRow label="Ad Soyad" value={employee.full_name} />
                <InfoRow label="Personel No" value={employee.employee_no} />
                <InfoRow label="Telefon" value={employee.phone || '—'} />
                <InfoRow label="E-posta" value={employee.email || '—'} />
                <InfoRow label="TC Kimlik No" value={employee.identity_no || '—'} />
                <InfoRow label="Adres" value={employee.address || '—'} />
              </div>
            </div>
            <div className="stat-card">
              <h3 className="text-sm font-semibold text-foreground-800 mb-4">İş Bilgileri</h3>
              <div className="space-y-3">
                <InfoRow label="Pozisyon" value={employee.position || '—'} />
                <InfoRow label="Departman" value={employee.department || '—'} />
                <InfoRow label="İşe Giriş Tarihi" value={employee.hire_date ? new Date(employee.hire_date).toLocaleDateString('tr-TR') : '—'} />
                <InfoRow label="Maaş" value={formatTRY(employee.salary || 0)} />
                <InfoRow label="Ödeme Günü" value={`Ayın ${employee.salary_payment_day || 1}'i`} />
                <InfoRow label="Çalışma Durumu" value={<Badge badgeClass={staffStatus.badgeClass(employee.status)} icon={staffStatus.icon(employee.status)}>{employee.status}</Badge>} />
              </div>
            </div>
            <div className="stat-card">
              <h3 className="text-sm font-semibold text-foreground-800 mb-4">Acil Durum</h3>
              <div className="space-y-3">
                <InfoRow label="Kişi" value={employee.emergency_contact_name || '—'} />
                <InfoRow label="Telefon" value={employee.emergency_contact_phone || '—'} />
              </div>
            </div>
            <div className="stat-card">
              <h3 className="text-sm font-semibold text-foreground-800 mb-4">Ödeme Özeti</h3>
              <div className="space-y-3">
                <InfoRow label="Toplam Ödenen" value={formatTRY(totalPaid)} />
                <InfoRow label="Toplam Avans" value={formatTRY(totalAdvance)} />
                <InfoRow label="Toplam Mesai" value={formatTRY(totalOvertime)} />
                <InfoRow label="Ödeme Sayısı" value={payments.length} />
              </div>
            </div>
            {employee.notes && (
              <div className="stat-card md:col-span-2">
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">Notlar</h3>
                <p className="text-sm text-foreground-500 whitespace-pre-wrap">{employee.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="stat-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground-800">Maaş ve Ödemeler ({payments.length})</h3>
              <button onClick={() => setShowPayment(true)} className="btn-primary cursor-pointer whitespace-nowrap text-sm flex items-center gap-1.5"><i className="ri-add-line"></i>Yeni Ödeme</button>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-background-50 rounded-lg p-3 text-center"><p className="text-xs text-foreground-400">Toplam Ödenen</p><p className="text-base font-bold text-foreground-900">{formatTRY(totalPaid)}</p></div>
              <div className="bg-background-50 rounded-lg p-3 text-center"><p className="text-xs text-foreground-400">Avans</p><p className="text-base font-bold text-amber-600">{formatTRY(totalAdvance)}</p></div>
              <div className="bg-background-50 rounded-lg p-3 text-center"><p className="text-xs text-foreground-400">Mesai</p><p className="text-base font-bold text-primary-600">{formatTRY(totalOvertime)}</p></div>
              <div className="bg-background-50 rounded-lg p-3 text-center hidden md:block"><p className="text-xs text-foreground-400">Kayıt</p><p className="text-base font-bold text-foreground-900">{payments.length}</p></div>
            </div>
            {payments.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-8">Henüz ödeme kaydı yok</p>
            ) : (
              <div className="space-y-2">
                {payments.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-background-50 rounded-lg border border-background-200/60">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-money-dollar-circle-line text-emerald-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{p.payment_type}</p>
                        <p className="text-xs text-foreground-400">{p.payment_date ? new Date(p.payment_date).toLocaleDateString('tr-TR') : '—'} · {p.payment_method}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-foreground-800">{formatTRY(p.amount || 0)}</span>
                      <button onClick={() => handleDeletePayment(p.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer"><i className="ri-delete-bin-line text-sm text-red-400"></i></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'leaves' && (
          <div className="stat-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground-800">İzin Kayıtları ({leaves.length})</h3>
              <button onClick={() => setShowLeave(true)} className="btn-primary cursor-pointer whitespace-nowrap text-sm flex items-center gap-1.5"><i className="ri-add-line"></i>İzin Ekle</button>
            </div>
            {leaves.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-8">Henüz izin kaydı yok</p>
            ) : (
              <div className="space-y-2">
                {leaves.map(l => (
                  <div key={l.id} className="flex items-center justify-between p-3 bg-background-50 rounded-lg border border-background-200/60">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-check-line text-amber-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{l.leave_type}</p>
                        <p className="text-xs text-foreground-400">{l.start_date ? new Date(l.start_date).toLocaleDateString('tr-TR') : '—'} - {l.end_date ? new Date(l.end_date).toLocaleDateString('tr-TR') : '—'} ({l.total_days} gün)</p>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteLeave(l.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer"><i className="ri-delete-bin-line text-sm text-red-400"></i></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'overtimes' && (
          <div className="stat-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground-800">Fazla Mesai Kayıtları ({overtimes.length})</h3>
              <button onClick={() => setShowOvertime(true)} className="btn-primary cursor-pointer whitespace-nowrap text-sm flex items-center gap-1.5"><i className="ri-add-line"></i>Mesai Ekle</button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-background-50 rounded-lg p-3 text-center"><p className="text-xs text-foreground-400">Toplam Saat</p><p className="text-base font-bold text-foreground-900">{overtimes.reduce((s: number, o: any) => s + parseFloat(o.hours || '0'), 0)} saat</p></div>
              <div className="bg-background-50 rounded-lg p-3 text-center"><p className="text-xs text-foreground-400">Toplam Ücret</p><p className="text-base font-bold text-primary-600">{formatTRY(totalOvertime)}</p></div>
            </div>
            {overtimes.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-8">Henüz mesai kaydı yok</p>
            ) : (
              <div className="space-y-2">
                {overtimes.map(o => (
                  <div key={o.id} className="flex items-center justify-between p-3 bg-background-50 rounded-lg border border-background-200/60">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-timer-line text-blue-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{o.overtime_date ? new Date(o.overtime_date).toLocaleDateString('tr-TR') : '—'}</p>
                        <p className="text-xs text-foreground-400">{o.hours} saat · {o.description || '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-foreground-800">{formatTRY(o.amount || 0)}</span>
                      <button onClick={() => handleDeleteOvertime(o.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer"><i className="ri-delete-bin-line text-sm text-red-400"></i></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'sales' && (
          <div className="stat-card">
            <h3 className="text-sm font-semibold text-foreground-800 mb-4">Satış Kayıtları ({sales.length})</h3>
            {sales.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-8">Henüz satış kaydı yok</p>
            ) : (
              <div className="hidden lg:block overflow-x-auto -mx-5">
                <table className="w-full min-w-[600px]">
                  <thead><tr className="border-b border-background-200"><th className="table-header">Satış No</th><th className="table-header">Müşteri</th><th className="table-header">Tarih</th><th className="table-header">Tutar</th><th className="table-header">Ödeme</th><th className="table-header">Teslimat</th></tr></thead>
                  <tbody>
                    {sales.map(s => (
                      <tr key={s.id} className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer" onClick={() => navigate(`/sales/${s.id}`)}>
                        <td className="table-cell font-medium text-primary-600">{s.sale_no}</td>
                        <td className="table-cell">{s.customer_name}</td>
                        <td className="table-cell text-foreground-500">{s.sale_date}</td>
                        <td className="table-cell font-medium">{formatTRY(s.total_amount || 0)}</td>
                        <td className="table-cell"><Badge badgeClass={paymentStatus.badgeClass(s.payment_status)} icon={paymentStatus.icon(s.payment_status)}>{s.payment_status}</Badge></td>
                        <td className="table-cell"><Badge badgeClass={deliveryStatus.badgeClass(s.delivery_status)} icon={deliveryStatus.icon(s.delivery_status)}>{s.delivery_status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Mobile sales */}
            <div className="lg:hidden space-y-2">
              {sales.map(s => (
                <div key={s.id} onClick={() => navigate(`/sales/${s.id}`)} className="bg-background-50 border border-background-200/70 rounded-lg p-3 cursor-pointer">
                  <div className="flex items-center justify-between"><span className="text-sm font-semibold text-primary-600">{s.sale_no}</span><Badge badgeClass={paymentStatus.badgeClass(s.payment_status)} icon={paymentStatus.icon(s.payment_status)}>{s.payment_status}</Badge></div>
                  <div className="flex items-center justify-between mt-1"><span className="text-xs text-foreground-500">{s.customer_name}</span><span className="text-sm font-semibold">{formatTRY(s.total_amount || 0)}</span></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'deliveries' && (
          <div className="stat-card">
            <h3 className="text-sm font-semibold text-foreground-800 mb-4">Teslimat Kayıtları ({deliveries.length})</h3>
            {deliveries.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-8">Henüz teslimat kaydı yok</p>
            ) : (
              <div className="hidden lg:block overflow-x-auto -mx-5">
                <table className="w-full min-w-[600px]">
                  <thead><tr className="border-b border-background-200"><th className="table-header">Teslimat No</th><th className="table-header">Müşteri</th><th className="table-header">Tarih</th><th className="table-header">Adres</th><th className="table-header">Durum</th></tr></thead>
                  <tbody>
                    {deliveries.map(d => (
                      <tr key={d.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                        <td className="table-cell text-foreground-500">{d.sale_no}</td>
                        <td className="table-cell font-medium">{d.customer_name}</td>
                        <td className="table-cell text-foreground-500">{d.delivery_date}</td>
                        <td className="table-cell text-xs text-foreground-500 truncate max-w-[200px]">{d.address}</td>
                        <td className="table-cell"><Badge badgeClass={deliveryStatus.badgeClass(d.status)} icon={deliveryStatus.icon(d.status)}>{d.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="lg:hidden space-y-2">
              {deliveries.map(d => (
                <div key={d.id} className="bg-background-50 border border-background-200/70 rounded-lg p-3">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium">{d.customer_name}</span><Badge badgeClass={deliveryStatus.badgeClass(d.status)} icon={deliveryStatus.icon(d.status)}>{d.status}</Badge></div>
                  <p className="text-xs text-foreground-400 mt-1">{d.delivery_date} · {d.sale_no}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="stat-card">
            <h3 className="text-sm font-semibold text-foreground-800 mb-4">Aktivite Geçmişi ({activities.length})</h3>
            {activities.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-8">Henüz aktivite kaydı yok</p>
            ) : (
              <div className="space-y-1">
                {activities.map((a, idx) => (
                  <div key={`${a.id}-${idx}`} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-background-50 transition-colors">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${a.color}`}>
                      <i className={`${a.icon} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-800">{a.title}</p>
                      <p className="text-xs text-foreground-400">{a.description}</p>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{a.date ? new Date(a.date).toLocaleDateString('tr-TR') : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <EmployeeFormModal isOpen={showEdit} onClose={() => setShowEdit(false)} onSave={handleEdit} initialData={employee} />
      <EmployeePaymentModal isOpen={showPayment} onClose={() => setShowPayment(false)} onSave={handlePayment} employeeName={employee.full_name} employeeId={employee.id} />
      <EmployeeLeaveModal isOpen={showLeave} onClose={() => setShowLeave(false)} onSave={handleLeave} employeeName={employee.full_name} />
      <EmployeeOvertimeModal isOpen={showOvertime} onClose={() => setShowOvertime(false)} onSave={handleOvertime} employeeName={employee.full_name} />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-background-100 last:border-0">
      <span className="text-xs text-foreground-400 whitespace-nowrap">{label}</span>
      <span className="text-sm text-foreground-700 text-right">{value}</span>
    </div>
  );
}